import type { TicketListQuery } from "@/api/support.api";
import type { SupportSummary, SupportSummaryGroup } from "@/types/domain";

/**
 * Who raised the tickets. The Support page switches between the two with a toggle; both are
 * the same tickets, filtered by `source`, so switching never moves or changes anything.
 */
export type SupportSource = "RIDER" | "DRIVER";

/**
 * The topics an admin can filter by. For riders: the topics of the rider app's Help screen, in the same
 * order, plus the driver reports (raised from a finished ride) and anything without a topic. For drivers:
 * the topics of the driver app's help. Each maps onto the ticket list filters and onto the keys of
 * GET /admin/support/summary.
 */
export type SupportGroupKey =
  // rider
  | "ALL"
  | "LOST_ITEM"
  | "PAYMENT_REFUND"
  | "ACCOUNT"
  | "SOS"
  | "APP_BUG"
  | "GENERAL"
  | "DRIVER_REPORT"
  | "UNCATEGORISED"
  // driver
  | "DRIVER_ALL"
  | "DRV_EARNINGS"
  | "DRV_DOCUMENTS"
  | "DRV_TRIP_FARE"
  | "DRV_REPORT_RIDER"
  | "DRV_ACCOUNT"
  | "DRV_APP_BUG"
  | "DRV_OTHER"
  | "DRV_CHAT";

export interface SupportGroup {
  key: SupportGroupKey;
  source: SupportSource;
  label: string;
  /** Ticket list filters for this topic. */
  query: Pick<TicketListQuery, "category" | "sos">;
  /** Summary keys that belong to this topic; `null` = all of this source's. */
  summaryKeys: string[] | null;
}

export const DRIVER_REPORT_CATEGORIES = ["UNSAFE_DRIVING", "RUDE_BEHAVIOUR", "OVERCHARGED", "VEHICLE_CONDITION", "OTHER"] as const;

const rider = (key: SupportGroupKey, label: string, query: SupportGroup["query"], summaryKeys: string[] | null): SupportGroup => ({
  key,
  source: "RIDER",
  label,
  query,
  summaryKeys,
});
const driver = (key: SupportGroupKey, label: string, category?: string): SupportGroup => ({
  key,
  source: "DRIVER",
  label,
  query: category ? { category } : {},
  summaryKeys: category ? [category] : null,
});

export const SUPPORT_GROUPS: SupportGroup[] = [
  // ── riders ──
  rider("ALL", "All topics", {}, null),
  rider("LOST_ITEM", "My last ride", { category: "LOST_ITEM" }, ["LOST_ITEM"]),
  rider("PAYMENT_REFUND", "Payments & refunds", { category: "PAYMENT_REFUND" }, ["PAYMENT_REFUND"]),
  rider("ACCOUNT", "Account & profile", { category: "ACCOUNT" }, ["ACCOUNT"]),
  rider("SOS", "Safety concern & SOS", { sos: true }, ["SOS"]),
  rider("APP_BUG", "Report a bug", { category: "APP_BUG" }, ["APP_BUG"]),
  rider("DRIVER_REPORT", "Driver reports", { category: DRIVER_REPORT_CATEGORIES.join(",") }, [...DRIVER_REPORT_CATEGORIES]),
  // Live chats (opened over the socket, answered on the Live chat tab). They are tickets too, so they stay reachable here.
  rider("GENERAL", "Live chat", { category: "GENERAL" }, ["GENERAL"]),
  rider("UNCATEGORISED", "Other", { category: "NONE" }, ["UNCATEGORISED"]),
  // ── drivers ──
  driver("DRIVER_ALL", "All topics"),
  driver("DRV_EARNINGS", "Earnings & withdrawals", "DRV_EARNINGS"),
  // driver("DRV_DOCUMENTS", "Documents & vehicle", "DRV_DOCUMENTS"), // switched off for now (also in the backend topic list)
  driver("DRV_TRIP_FARE", "Trip & fare issues", "DRV_TRIP_FARE"),
  driver("DRV_REPORT_RIDER", "Report a rider", "DRV_REPORT_RIDER"),
  driver("DRV_ACCOUNT", "Account & profile", "DRV_ACCOUNT"),
  driver("DRV_APP_BUG", "App issues & bugs", "DRV_APP_BUG"),
  driver("DRV_OTHER", "Other", "DRV_OTHER"),
  driver("DRV_CHAT", "Live chat", "DRV_CHAT"),
];

/** The groups of one side, in display order. */
export const groupsOf = (source: SupportSource): SupportGroup[] => SUPPORT_GROUPS.filter((g) => g.source === source);

/** The "all topics" group of a side: where the page starts when the toggle flips. */
export const defaultGroupKey = (source: SupportSource): SupportGroupKey => (source === "DRIVER" ? "DRIVER_ALL" : "ALL");

/** Topics that only get a chip while some ticket is in them. */
const ONLY_WHEN_USED: SupportGroupKey[] = ["GENERAL", "DRV_CHAT", "UNCATEGORISED"];

/** `category` values of the live chats: the riders' and the drivers'. */
export const CHAT_CATEGORIES = ["GENERAL", "DRV_CHAT"] as const;

/** What the Live chat tab badges: chats whose last word is the person's, and chats support has answered. */
export function chatCounts(summary: SupportSummary | undefined): { unanswered: number; answered: number } {
  const chats = (summary?.groups ?? []).filter((g) => (CHAT_CATEGORIES as readonly string[]).includes(g.key));
  return { unanswered: chats.reduce((n, g) => n + g.open, 0), answered: chats.reduce((n, g) => n + g.waiting, 0) };
}

/**
 * What admins see as a ticket's status, mapped onto the stored `TicketStatus`:
 * Unresolved = `OPEN` (new, or the person wrote back), In progress = `WAITING`
 * (an admin replied or took it), Resolved = `RESOLVED`.
 */
export type SupportStatusFilter = "OPEN" | "WAITING" | "RESOLVED" | "ALL";

export interface StatusCounts {
  /** Unresolved. */
  open: number;
  /** In progress. */
  waiting: number;
  resolved: number;
}

const EMPTY: StatusCounts = { open: 0, waiting: 0, resolved: 0 };

/** Summary rows of one side. A row without a `source` (an older backend) is a rider row. */
const rowsOf = (summary: SupportSummary | undefined, source: SupportSource): SupportSummaryGroup[] =>
  (summary?.groups ?? []).filter((g) => (g.source ?? "RIDER") === source);

const sum = (rows: SupportSummaryGroup[]): StatusCounts =>
  rows.reduce<StatusCounts>((acc, g) => ({ open: acc.open + g.open, waiting: acc.waiting + g.waiting, resolved: acc.resolved + g.resolved }), EMPTY);

/** Adds up the summary rows that belong to a topic of its side (all of the side's rows for "All topics"). */
export function groupCounts(summary: SupportSummary | undefined, group: SupportGroup): StatusCounts {
  return sum(rowsOf(summary, group.source).filter((g) => group.summaryKeys === null || group.summaryKeys.includes(g.key)));
}

/** Everything on one side — what the Riders / Drivers toggle badges. */
export function sourceCounts(summary: SupportSummary | undefined, source: SupportSource): StatusCounts {
  return sum(rowsOf(summary, source));
}

/** How many tickets a status filter shows, given a topic's counts. The topic chips badge `OPEN` — what still needs an admin. */
export function statusCount(c: StatusCounts, status: SupportStatusFilter): number {
  switch (status) {
    case "OPEN":
      return c.open;
    case "WAITING":
      return c.waiting;
    case "RESOLVED":
      return c.resolved;
    default:
      return c.open + c.waiting + c.resolved;
  }
}

/** Topics to offer as chips for a side: all the current ones, plus "Live chat" and "Other" only while tickets are in them. */
export function visibleGroups(summary: SupportSummary | undefined, source: SupportSource = "RIDER"): SupportGroup[] {
  return groupsOf(source).filter((g) => !ONLY_WHEN_USED.includes(g.key) || statusCount(groupCounts(summary, g), "ALL") > 0);
}
